import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Box } from '@hub/box'
import { Text } from '@hub/text'
import {
  DeleteCustomerMobileNumberDocument,
  UpdateCustomerMobileNumberDocument,
} from '@scentregroup/shared/graphql'
import useMobileBreakpoint from '@scentregroup/shared/helpers/use-mobile-breakpoint'
import useForm, {
  IFormErrors,
  IFormValues,
} from '@scentregroup/shared/hooks/use-form'
import { FormInput, FormSelect } from '@scentregroup/shared/hub-components/form'

import { ButtonLink } from '../button'
import { useSafeMutation } from '../../lib/apollo-client'
import { updatingMobileNumberAtom } from '../../lib/atoms/update-mobile'
import { userAtom } from '../../lib/atoms/user-info'
import { validateMobile } from '../../lib/form'
import { onError } from '../../lib/on-error'
import useDialog from '../../lib/use-dialog'
import useNotification from '../../lib/use-notification'
import { useProfileEdit } from '../../lib/use-profile-edit'
import { ProfileSection } from '../../types/profile'
import ProfileLayout from './profile-layout'
import {
  ERROR_REMOVE_MOBILE,
  ERROR_UPDATE_MOBILE,
  ERROR_UPDATE_MOBILE_TOO_MANY_ATTEMPTS,
} from '../../lib/error-messages'
import { MESSAGE_MOBILE_NUMBER_REMOVED } from '../../lib/notification-messages'
import { useRouter } from 'next/router'
import { CHECK_MOBILE, PROFILE } from '../../lib/paths'
import { addSpacing } from '../../lib/mobile-number'

type Mobile = {
  country: string
  mobile: string
}

type profileMobileErrors = IFormErrors<Mobile>
type profileMobileValues = IFormValues<Mobile>

enum ProfileMobileFields {
  COUNTRY = 'country',
  MOBILE = 'mobile',
}

const MOBILE_NOT_PROVIDED = 'Not provided'

const AU = 'AU'
const NZ = 'NZ'
const AU_CODE = '+61'
const NZ_CODE = '+64'

const ProfileMobile: React.FC<React.PropsWithChildren<unknown>> = () => {
  const isMobile = useMobileBreakpoint()
  const router = useRouter()
  const { openNotification } = useNotification()
  const { openDialog } = useDialog()
  const { data: user, refetch } = useRecoilValue(userAtom)

  const [updateCustomerMobileNumber] = useSafeMutation(
    UpdateCustomerMobileNumberDocument
  )
  const [deleteCustomerMobileNumber] = useSafeMutation(
    DeleteCustomerMobileNumberDocument
  )

  const profileMobile = useMemo(() => user?.mobile, [user])
  const formattedMobile = useMemo(
    () => addSpacing(profileMobile || ''),
    [profileMobile]
  )
  const [isSaving, setIsSaving] = useState(false)
  const setUpdatingMobileNumber = useSetRecoilState(updatingMobileNumberAtom)

  const { cancelEdit } = useProfileEdit(ProfileSection.MOBILE)

  const COUNTRY_VALUE_SELECTIONS = useMemo(
    () => [
      {
        label: isMobile ? `${AU} ${AU_CODE}` : `Australia ${AU_CODE}`,
        value: AU,
      },
      {
        label: isMobile ? `${NZ} ${NZ_CODE}` : `New Zealand ${NZ_CODE}`,
        value: NZ,
      },
    ],
    [isMobile]
  )

  const readCountry = useCallback((mobileNum: string) => {
    return mobileNum.substring(0, 3) === AU_CODE ? AU : NZ
  }, [])

  const readMobile = useCallback((mobileNum: string) => {
    return `0${mobileNum.substring(3)}`
  }, [])

  const defaultProfileMobile = useMemo(
    () =>
      profileMobile
        ? {
            country: profileMobile ? readCountry(profileMobile) : AU,
            mobile: profileMobile ? readMobile(profileMobile) : '',
          }
        : {
            country: profileMobile ? readCountry(profileMobile) : AU,
          },
    [profileMobile, readCountry, readMobile]
  )

  const validateProfileMobile = useCallback(
    (
      values: profileMobileValues,
      isSubmitting: boolean | undefined
    ): profileMobileErrors => {
      const errors: profileMobileErrors = {}
      if ('mobile' in values || isSubmitting) {
        errors[ProfileMobileFields.MOBILE] = validateMobile(values.mobile)
      }

      return errors
    },
    []
  )

  const createMobileNumber = useCallback(({ country, mobile }: Mobile) => {
    const cleanMobile: string =
      mobile.substr(0, 1) === '0' ? mobile.substr(1) : mobile

    return country === AU
      ? `${AU_CODE}${cleanMobile}`
      : `${NZ_CODE}${cleanMobile}`
  }, [])

  const onSave = useCallback(
    async (values: Mobile) => {
      setIsSaving(true)

      try {
        const mobile = createMobileNumber(values)

        await updateCustomerMobileNumber({
          variables: {
            input: {
              mobile: mobile,
            },
          },
        })
        if (refetch) await refetch()

        setUpdatingMobileNumber(mobile)
        router.push(`/${PROFILE}/${CHECK_MOBILE}`)
        cancelEdit()
      } catch (error) {
        onError(
          error,
          openNotification,
          error.message.includes('Too Many Requests')
            ? ERROR_UPDATE_MOBILE_TOO_MANY_ATTEMPTS
            : ERROR_UPDATE_MOBILE
        )
      } finally {
        setIsSaving(false)
      }
    },
    [
      cancelEdit,
      createMobileNumber,
      openNotification,
      refetch,
      setUpdatingMobileNumber,
      updateCustomerMobileNumber,
      router,
    ]
  )

  const {
    errors,
    values,
    hasChanges,
    resetValues,
    clearErrors,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useForm<Mobile>(validateProfileMobile, onSave, defaultProfileMobile)

  const onConfirmDeleteMobile = useCallback(async (): Promise<void> => {
    try {
      await deleteCustomerMobileNumber()
      resetValues([ProfileMobileFields.COUNTRY, ProfileMobileFields.MOBILE])
      if (refetch) await refetch()
      cancelEdit()
      openNotification(MESSAGE_MOBILE_NUMBER_REMOVED)
    } catch (error) {
      onError(error, openNotification, ERROR_REMOVE_MOBILE)
    }
  }, [
    deleteCustomerMobileNumber,
    refetch,
    resetValues,
    cancelEdit,
    openNotification,
  ])

  const showDeleteMobileConfirm = useCallback(async (): Promise<void> => {
    openDialog({
      heading: 'Remove mobile number',
      message: `You will no longer receive notifications from Westfield to the number ${formattedMobile}.`,
      action: {
        label: 'Remove',
        onClick: () => onConfirmDeleteMobile(),
      },
      dataTestId: 'remove-mobile-number',
    })
  }, [formattedMobile, openDialog, onConfirmDeleteMobile])

  const onCancel = useCallback(() => {
    resetValues([ProfileMobileFields.COUNTRY, ProfileMobileFields.MOBILE])
    clearErrors()
  }, [resetValues, clearErrors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    clearErrors()
    handleChange({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })
  }

  const onBlur = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void =>
    handleBlur({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })

  useEffect(() => {
    resetValues([ProfileMobileFields.COUNTRY, ProfileMobileFields.MOBILE])
  }, [defaultProfileMobile, resetValues])

  return (
    <ProfileLayout
      section={ProfileSection.MOBILE}
      title={'Mobile number'}
      readOnlyView={
        <Text>{`${
          formattedMobile ? formattedMobile : MOBILE_NOT_PROVIDED
        }`}</Text>
      }
      handleSubmit={handleSubmit}
      handleCancel={onCancel}
      hasChanges={hasChanges}
      isSubmitting={isSaving}
    >
      <Box display={'flex'} sx={{ gap: ['16px', '16px', '32px'] }}>
        <FormSelect
          id={'country'}
          label={'Country code'}
          name={ProfileMobileFields.COUNTRY}
          value={values.country || ''}
          data={COUNTRY_VALUE_SELECTIONS}
          onChange={onChange}
          onBlur={onBlur}
          width={['110px', '110px', '220px']}
          minHeight={'42px'}
        />
        <FormInput
          id={'mobile'}
          label={'Mobile number'}
          name={ProfileMobileFields.MOBILE}
          type={'tel'}
          inputMode={'tel'}
          autoComplete={'tel'}
          autoCorrect={'off'}
          value={values.mobile || ''}
          isInvalid={!!errors.mobile}
          errorMessage={errors.mobile}
          isLoading={false}
          onChange={onChange}
          onBlur={onBlur}
        />
      </Box>
      {profileMobile && (
        <ButtonLink onClick={showDeleteMobileConfirm}>
          Remove mobile number
        </ButtonLink>
      )}
    </ProfileLayout>
  )
}

export default ProfileMobile
